import { useEffect, useState } from 'react';
import { CheckIconV2, CloseIcon, MicrophoneIcon, ProgressSpinner } from '@repo/atomic-components';
import { ToolbarChipButton } from './ToolbarChipButton';

// Four bars with staggered delays make a live-looking soundwave. Kept as
// a module-scoped constant so React doesn't re-instantiate the array on
// every render of the listening pill.
const SOUNDWAVE_BAR_DELAYS_MS = [0, 120, 240, 360];

/**
 * Live m:ss stopwatch. Rendered inside the listening pill so it only
 * mounts while dictation is active — the interval is set on mount and
 * cleared on unmount, so it stops the moment the pill hides (Done /
 * Cancel), and every new session starts fresh at 0:00 rather than
 * resuming a running counter.
 */
const DictationTimer = () => {
  const [elapsedMs, setElapsedMs] = useState(0);
  useEffect(() => {
    const startedAt = performance.now();
    // 250ms is dense enough that the seconds tick reliably (no more than
    // a 1s beat is ever missed on a lagging tab) but not so tight that we
    // schedule unnecessary re-renders — the display only changes at 1s
    // resolution anyway.
    const interval = window.setInterval(() => {
      setElapsedMs(performance.now() - startedAt);
    }, 250);
    return () => window.clearInterval(interval);
  }, []);
  const totalSeconds = Math.floor(elapsedMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return (
    <span className="text-fletchers-red-900 text-[12px] font-medium tabular-nums">
      {minutes}:{seconds.toString().padStart(2, '0')}
    </span>
  );
};

export interface DictationControlProps {
  /** Whether the browser supports SpeechRecognition. When false, nothing renders. */
  isSupported: boolean;
  /** True while a dictation session is active — swaps the chip for the live pill. */
  isListening: boolean;
  /** True while the recogniser is tearing down after Done — shows a spinner in the chip. */
  isBusy: boolean;
  onStart: () => void;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Two-state dictation UI:
 *   • Idle → 24x24 mic chip button (matches other toolbar chips).
 *   • Listening → an expanding pale-red pill with a live soundwave, m:ss
 *     stopwatch, and bare tick / cross icon actions.
 *
 * Assumes the `useDictation` hook drives it — pass its returned state and
 * callbacks straight through. Kept dumb so any consumer (chronology
 * dialogs, chat inputs, notes fields) can host the same visual.
 */
export const DictationControl = ({
  isSupported,
  isListening,
  isBusy,
  onStart,
  onCancel,
  onConfirm,
}: DictationControlProps) => {
  if (!isSupported) return null;

  if (isListening) {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-label="Dictation in progress"
        className="bg-fletchers-red-50 flex h-6 origin-left items-center gap-2 rounded-md px-2"
        style={{ animation: 'dictation-listening-in 180ms ease-out both' }}
      >
        <MicrophoneIcon width={12} height={12} className="text-fletchers-red-900 shrink-0" />
        {/* Soundwave — 4 bars pulsing at staggered offsets so the motion
            reads as audio activity rather than a mechanical loading
            indicator. */}
        <div aria-hidden className="flex items-center gap-[2px]">
          {SOUNDWAVE_BAR_DELAYS_MS.map((delay) => (
            <span
              key={delay}
              className="bg-fletchers-red-900 block h-[10px] w-[2px] rounded-full"
              style={{
                animation: 'dictation-soundwave 900ms ease-in-out infinite',
                animationDelay: `${delay}ms`,
              }}
            />
          ))}
        </div>
        <DictationTimer />
        {/* Cross + tick as bare icon buttons — no chip / circle wrapper.
            Rely on the icon colour + `title` for meaning; small `p-0.5`
            hit target keeps the tap area usable without adding visual
            weight. `currentColor` on the tick lets the button's text-*
            class drive both resting and hover colours. */}
        <button
          type="button"
          onClick={onCancel}
          title="Cancel dictation"
          aria-label="Cancel dictation"
          className="text-fletchers-red-900 hover:text-fletchers-red-600 flex shrink-0 cursor-pointer items-center justify-center p-0.5 focus:outline-none"
        >
          <CloseIcon width={10} height={10} />
        </button>
        <button
          type="button"
          onClick={onConfirm}
          title="Insert dictated text"
          aria-label="Insert dictated text"
          className="text-fletchers-red-900 hover:text-fletchers-red-600 flex shrink-0 cursor-pointer items-center justify-center p-0.5 focus:outline-none"
        >
          <CheckIconV2 width={12} height={10} />
        </button>
      </div>
    );
  }

  return (
    <ToolbarChipButton
      onClick={onStart}
      disabled={isBusy}
      title="Dictate"
      aria-label="Start dictation"
      className={isBusy ? '!cursor-wait' : undefined}
    >
      {isBusy ? (
        <ProgressSpinner style={{ width: '14px', height: '14px' }} strokeWidth="4" animationDuration="1.5s" />
      ) : (
        <MicrophoneIcon width={14} height={14} />
      )}
    </ToolbarChipButton>
  );
};
